import {
  Button,
  Grid,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import React from "react";
import { useIntl } from "react-intl";
import { useGetAllModulesAttachmentsQuery } from "../../../../api/rtk/SignUpApi";
import { ModuleReference } from "../../../../model/CertificationDataContextModel";
import { DocumentReferenceViewModel } from "../../../../model/DocumentReferenceViewModel";
import {
  ModuleSelectionDetails,
  ModuleSelectionViewModel,
} from "../../../../model/ExamSignup/ModuleSelectionViewModel";
import { GetTranslatedValueIntl } from "../../../../model/Shared/TranslatedValue";
import TranslatedMessage from "../../../translations/TranslatedMessage";
import { useSignUpContext } from "../../CertificationDataWizardContext/CertificationDataWizardContext";
import { ControlButtons } from "../shared/ControlButtons";
import ModuleDetailsDialog from "./ModuleDetailsDialog";
import { ExamType } from "../../../../model/Exam";
import StateIcon from "../../../common/icons/StateIcon";
import { StateIconType } from "../../../common/icons/StateIcon";

export type ModuleData = {
  selection: ModuleSelectionViewModel;
  reference: ModuleReference;
  courseName: string;
  examSessionName: string;
  attachments: DocumentReferenceViewModel[];
  paid: boolean;
};

const useStyles = makeStyles({
  button: { textTransform: "none", whiteSpace: "nowrap" },
});

const ModulesDetailsStep: React.FunctionComponent = () => {
  const {
    availableExamSessions,
    referenceCourses,
    referenceModules,
    data: { modulesSelection },
    setValues,
    handleNextStep,
  } = useSignUpContext();

  const [dialogModuleindex, setDialogModuleIndex] = React.useState<number | undefined>(undefined);

  const { data: allModulesAttachments } = useGetAllModulesAttachmentsQuery();

  const intl = useIntl();
  const classes = useStyles();

  const getCourseName = (courseId: string) => {
    const course = referenceCourses.find((c) => c.id === courseId);
    return course ? GetTranslatedValueIntl(intl, course.name) : courseId;
  };

  const getExamSessionName = (examSessionId: string) => {
    const examSession = availableExamSessions.find((e) => e.id === examSessionId);
    return examSession ? GetTranslatedValueIntl(intl, examSession.sessionKey) : examSessionId;
  };

  const moduleItems = modulesSelection
    .map((module) => {
      const moduleReference = referenceModules.find(
        (m) => m.examSessionId === module.examSessionId && m.courseId === module.courseId && m.id === module.moduleId
      );

      return moduleReference
        ? ({
            isValid: false,
            selection: module,
            reference: moduleReference,
            courseName: getCourseName(module.courseId),
            examSessionName: getExamSessionName(module.examSessionId),
            attachments: allModulesAttachments?.find((ma) => ma.moduleId === module.moduleId)?.attachments ?? [],
            paid: module.paid,
          } as ModuleData)
        : undefined;
    })
    .filter((moduleItem): moduleItem is ModuleData => !!moduleItem);

  const handleDialogOk = (details: ModuleSelectionDetails) => {
    if (dialogModuleindex !== undefined) {
      const currentSelection = moduleItems[dialogModuleindex].selection;
      const newModulesSelection = modulesSelection.map((s) => {
        if (
          s.courseId === currentSelection.courseId &&
          s.examSessionId === currentSelection.examSessionId &&
          s.moduleId === currentSelection.moduleId
        ) {
          return { ...currentSelection, details };
        }
        return s;
      });
      setValues({ modulesSelection: newModulesSelection });
      setDialogModuleIndex(undefined);
    }
  };

  const hasCompleteData = (moduleData: ModuleData): boolean => {
    const isFullyBooked =
      !moduleData.paid &&
      (moduleData.selection.examDetails?.filter((e) => e.isFullyBooked && e.examType !== ExamType.TermPaper) ?? [])
        .length > 0;
    return (
      moduleData.selection.details !== undefined &&
      !isFullyBooked &&
      moduleData.selection.details?.trainingProvider !== undefined &&
      moduleData.selection.details?.trainingProvider !== "" &&
      (!moduleData.reference.needsOption ||
        (moduleData.selection.details?.option !== undefined && moduleData.selection.details?.option !== "")) &&
      moduleData.selection.details?.isRepeater !== undefined &&
      (!moduleData.reference.needsDocuments ||
        moduleData.attachments.length > 0 ||
        moduleData.selection.details?.willProvideDocumentsLater === true)
    );
  };

  const isInputComplete = moduleItems.reduce((prevIsValid, curr) => prevIsValid && hasCompleteData(curr), true);
  if (!modulesSelection){
    return null;
  }

  return (
    <React.Fragment>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Typography>
            <TranslatedMessage id="startCertification.modulesDetailsStep.helperText" />
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography style={{ paddingBottom: "8px" }}>
            <TranslatedMessage id="startCertification.modulesDetailsStep.tableTitle" />
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TranslatedMessage id="application.examLocationAndDate.moduleColumn" />
                  </TableCell>
                  <TableCell>
                    <TranslatedMessage id="application.userSelector.examSession" />
                  </TableCell>
                  <TableCell>
                    <TranslatedMessage id="course.header" />
                  </TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {moduleItems.map((moduleData, index) => {
                  const fullyBooked =
                    !moduleData.paid &&
                    (
                      moduleData.selection?.examDetails?.filter(
                        (d) => d.isFullyBooked && d.examType !== ExamType.TermPaper
                      ) ?? []
                    ).length > 0;
                  return (
                    <TableRow key={index}>
                      <TableCell>{GetTranslatedValueIntl(intl, moduleData.reference.name)}</TableCell>
                      <TableCell>{moduleData.examSessionName}</TableCell>
                      <TableCell>{moduleData.courseName}</TableCell>
                      <TableCell>
                        <Button
                          onClick={() => setDialogModuleIndex(index)}
                          startIcon={<EditIcon />}
                          variant="contained"
                          color="primary"
                          className={classes.button}
                          disabled={moduleData.paid || fullyBooked}
                        >
                          <TranslatedMessage id="startCertification.modulesDetailsStep.fillDetails" />
                        </Button>
                      </TableCell>
                      <TableCell>
                        {hasCompleteData(moduleData) ? (
                          <StateIcon state={moduleData.paid ? StateIconType.Paid : StateIconType.Completed} />
                        ) : (
                          <StateIcon state={fullyBooked ? StateIconType.FullyBooked : StateIconType.Incomplete} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <ControlButtons onNextClick={handleNextStep} nextDisabled={!isInputComplete} />

      {dialogModuleindex !== undefined && (
        <ModuleDetailsDialog
          moduleData={moduleItems[dialogModuleindex]}
          handleOk={handleDialogOk}
          handleCancel={() => setDialogModuleIndex(undefined)}
        />
      )}
    </React.Fragment>
  );
};

export default ModulesDetailsStep;
